import { useCallback } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import Button from "ds/components/Button";
import useTypedContext from "hooks/useTypedContext";
import useSetPrioritizeRun from "shared/Run/useSetPrioritizeRun";
import Tooltip from "ds/components/Tooltip";
import { AccessResult } from "utils/access";

import { useRunShortcut } from "../../hooks/useRunShortcut";

type PrioritizeRunButtonProps = {
  runId: string;
  stackId: string;
  isRunPrioritized: boolean;
  runQueryToRefetch: string;
  canPrioritize: AccessResult;
};

const PrioritizeRunButton = ({
  runId,
  stackId,
  isRunPrioritized,
  runQueryToRefetch,
  canPrioritize,
}: PrioritizeRunButtonProps) => {
  const { reportSuccess } = useTypedContext(FlashContext);

  const [setRunPriority, { loading }] = useSetPrioritizeRun({
    refetchQueries: [runQueryToRefetch],
  });

  const onClick = useCallback(
    () =>
      setRunPriority({ stack: stackId, run: runId, prioritize: !isRunPrioritized }, (data) => {
        if (data?.runPrioritizeSet.id) {
          reportSuccess({
            message: `Run ${data.runPrioritizeSet.id} priority successfully changed`,
          });
        }
      }),
    [setRunPriority, stackId, runId, isRunPrioritized, reportSuccess]
  );

  const label = isRunPrioritized ? "De-prioritize" : "Prioritize";

  useRunShortcut({
    label,
    code: "mod+alt+h",
    callback: onClick,
    loading,
    disabled: !canPrioritize.result,
  });

  if (!canPrioritize.result && !canPrioritize.message) {
    return null;
  }

  return (
    <Tooltip
      active={!!canPrioritize.message}
      on={(props) => (
        <Button
          {...props}
          variant="secondary"
          size="small"
          disabled={loading || !canPrioritize.result}
          loading={loading}
          onPress={onClick}
        >
          {label}
        </Button>
      )}
    >
      {canPrioritize.message}
    </Tooltip>
  );
};

export default PrioritizeRunButton;
